import { useState, useCallback, useEffect, useRef } from 'react';
import api from '@/api';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function useEditorActions(jobId, selectedSheet, { fetchJob, fetchSheetDetails }) {
  const [processingDocs, setProcessingDocs] = useState(false);
  const [canvasLoading, setCanvasLoading] = useState(false);
  const [autoLabeling, setAutoLabeling] = useState(false);
  const [labelProgress, setLabelProgress] = useState(null);
  const esRef = useRef(null);

  const sheetId = selectedSheet?.id;

  // Listen for labeling SSE events when auto-labeling is active
  useEffect(() => {
    if (!autoLabeling || !jobId) return;

    const token = localStorage.getItem('fmiq_token');
    if (!token) return;

    const url = `${API_URL}/drawing-jobs/${jobId}/progress/stream?token=${encodeURIComponent(token)}`;
    let es;
    try { es = new EventSource(url); } catch { return; }
    esRef.current = es;

    es.addEventListener('label_progress', (e) => {
      try {
        const data = JSON.parse(e.data);
        if (data.sheet_id === sheetId) {
          setLabelProgress(data);
        }
      } catch {}
    });

    es.addEventListener('label_complete', (e) => {
      try {
        const data = JSON.parse(e.data);
        if (data.sheet_id === sheetId) {
          toast.success(`Labeled ${data.labeled} of ${data.total} polygons`);
          setAutoLabeling(false);
          setLabelProgress(null);
          fetchSheetDetails();
          es.close();
        }
      } catch {}
    });

    es.onerror = () => { /* fallback will handle it */ };

    return () => { es.close(); esRef.current = null; };
  }, [autoLabeling, jobId, sheetId, fetchSheetDetails]);

  const handleProcessDocuments = useCallback(async () => {
    setProcessingDocs(true);
    try {
      await api.post(`/drawing-jobs/${jobId}/process-documents`);
      setTimeout(async () => {
        await fetchSheetDetails();
        setProcessingDocs(false);
      }, 3000);
    } catch (err) {
      console.error('Processing failed:', err);
      setProcessingDocs(false);
    }
  }, [jobId, fetchSheetDetails]);

  const handleReview = useCallback(async (entityType, entityId, action, fieldEdits = null) => {
    try {
      await api.post(`/drawing-jobs/${jobId}/review`, {
        decisions: [{ entity_type: entityType, entity_id: entityId, action, field_edits: fieldEdits }],
      });
      await Promise.all([fetchJob(), fetchSheetDetails()]);
    } catch (err) {
      console.error('Review failed:', err);
    }
  }, [jobId, fetchJob, fetchSheetDetails]);

  const handleCreatePolygon = useCallback(async (vertices) => {
    try {
      await api.post(`/drawing-jobs/${jobId}/sheets/${sheetId}/polygons`, { vertices });
      toast.success('Polygon created');
      await fetchSheetDetails();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create polygon');
    }
  }, [jobId, sheetId, fetchSheetDetails]);

  const handleUpdatePolygon = useCallback(async (polygonId, data) => {
    try {
      await api.put(`/drawing-jobs/${jobId}/sheets/${sheetId}/polygons/${polygonId}`, data);
      await fetchSheetDetails();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update polygon');
    }
  }, [jobId, sheetId, fetchSheetDetails]);

  const handleDeletePolygon = useCallback(async (polygonId) => {
    try {
      await api.delete(`/drawing-jobs/${jobId}/sheets/${sheetId}/polygons/${polygonId}`);
      toast.success('Polygon deleted');
      await fetchSheetDetails();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete polygon');
    }
  }, [jobId, sheetId, fetchSheetDetails]);

  const handleSeedDetect = useCallback(async (x, y) => {
    setCanvasLoading(true);
    try {
      await api.post(`/drawing-jobs/${jobId}/sheets/${sheetId}/polygons/bpoly-seed`, { x, y });
      toast.success('Room detected!');
      await fetchSheetDetails();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No room detected at that point');
    } finally {
      setCanvasLoading(false);
    }
  }, [jobId, sheetId, fetchSheetDetails]);

  const handleUpdateSpace = useCallback(async (spaceId, fieldEdits) => {
    try {
      await api.put(`/drawing-jobs/${jobId}/sheets/${sheetId}/spaces/${spaceId}`, { field_edits: fieldEdits });
      await fetchSheetDetails();
    } catch (err) {
      toast.error('Failed to update space');
    }
  }, [jobId, sheetId, fetchSheetDetails]);

  const handleCreateSpace = useCallback(async (data) => {
    try {
      await api.post(`/drawing-jobs/${jobId}/sheets/${sheetId}/spaces`, data);
      toast.success('Space added');
      await fetchSheetDetails();
    } catch (err) {
      toast.error('Failed to create space');
    }
  }, [jobId, sheetId, fetchSheetDetails]);

  const handleDeleteSpace = useCallback(async (spaceId) => {
    try {
      await api.delete(`/drawing-jobs/${jobId}/sheets/${sheetId}/spaces/${spaceId}`);
      toast.success('Space deleted');
      await fetchSheetDetails();
    } catch (err) {
      toast.error('Failed to delete space');
    }
  }, [jobId, sheetId, fetchSheetDetails]);

  const handlePublish = useCallback(async () => {
    try {
      await api.post(`/drawing-jobs/${jobId}/sheets/${sheetId}/publish`);
      toast.success('Sheet published! All spaces approved.');
      await fetchSheetDetails();
      await fetchJob();
    } catch (err) {
      toast.error('Failed to publish');
    }
  }, [jobId, sheetId, fetchSheetDetails, fetchJob]);

  const handleAutoLabel = useCallback(async () => {
    setAutoLabeling(true);
    setLabelProgress(null);
    try {
      const res = await api.post(`/drawing-jobs/${jobId}/sheets/${sheetId}/auto-label`);
      if (!res.data.started) {
        toast.info(res.data.message || `Already labeled: ${res.data.labeled}`);
        setAutoLabeling(false);
        return;
      }
      toast.info(`Auto-labeling ${res.data.total_unlabeled} polygons...`);
      // SSE listener (above useEffect) handles live updates.
      // Polling fallback if SSE doesn't fire within 10s
      const fallbackTimeout = setTimeout(() => {
        if (!esRef.current || esRef.current.readyState === EventSource.CLOSED) {
          const poll = setInterval(async () => {
            try {
              const details = await api.get(`/drawing-jobs/${jobId}/sheets/${sheetId}/details`);
              const status = details.data?.sheet?.labeling_status;
              if (status && !status.running) {
                clearInterval(poll);
                toast.success(`Labeled ${status.labeled} of ${status.total} polygons`);
                setAutoLabeling(false);
                setLabelProgress(null);
                await fetchSheetDetails();
              } else if (status) {
                setLabelProgress({ labeled: status.labeled, total: status.total });
              }
            } catch { /* keep polling */ }
          }, 3000);
        }
      }, 5000);
      return () => clearTimeout(fallbackTimeout);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Auto-labeling failed');
      setAutoLabeling(false);
    }
  }, [jobId, sheetId, fetchSheetDetails]);

  const handleDownload = useCallback(async (version) => {
    if (!selectedSheet) return;
    try {
      const res = await api.get(
        `/drawing-jobs/${jobId}/sheets/${sheetId}/download/${version}`,
        { responseType: 'blob' }
      );
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${selectedSheet.sheet_number}_${version}.png`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
    }
  }, [jobId, sheetId, selectedSheet]);

  const handleCleanupSpaces = useCallback(async () => {
    try {
      const res = await api.post(`/drawing-jobs/${jobId}/sheets/${sheetId}/cleanup-spaces`);
      const { orphans_removed, duplicates_removed, total_removed, restored, spaces_remaining } = res.data;
      if (total_removed > 0 || restored > 0) {
        const parts = [];
        if (total_removed > 0) parts.push(`removed ${total_removed} stale`);
        if (restored > 0) parts.push(`restored ${restored} missing`);
        toast.success(`Reconciled: ${parts.join(', ')}. ${spaces_remaining} spaces now.`);
      } else {
        toast.info('No orphaned or duplicate spaces found.');
      }
      await fetchSheetDetails();
      return res.data;
    } catch (err) {
      toast.error('Cleanup failed');
      return null;
    }
  }, [jobId, sheetId, fetchSheetDetails]);

  const handleClassifySpaceTypes = useCallback(async () => {
    try {
      toast.info('Classifying space types with AI...');
      const res = await api.post(`/drawing-jobs/${jobId}/sheets/${sheetId}/classify-space-types`);
      const { classified, total_unclassified } = res.data;
      if (classified > 0) {
        toast.success(`Classified ${classified} of ${total_unclassified} spaces.`);
      } else {
        toast.info(total_unclassified === 0 ? 'All spaces already have types.' : 'Could not classify any spaces.');
      }
      await fetchSheetDetails();
    } catch (err) {
      toast.error('Classification failed');
    }
  }, [jobId, sheetId, fetchSheetDetails]);

  const handleDetectOverlaps = useCallback(async (dryRun = true) => {
    if (!sheetId) return null;
    try {
      const res = await api.post(`/drawing-jobs/${jobId}/sheets/${sheetId}/detect-overlaps`, {
        iou_threshold: 0.3,
        dry_run: dryRun,
      });
      if (dryRun) {
        if (res.data.overlaps.length === 0) {
          toast.info('No overlapping polygons detected');
        } else {
          toast.info(`Found ${res.data.overlaps.length} overlap(s)`);
        }
      } else {
        if (res.data.merged > 0) {
          toast.success(`Merged ${res.data.merged} overlapping polygon(s)`);
          await fetchSheetDetails();
        } else {
          toast.info('No polygons were merged (labels may differ — only matching labels are auto-merged)');
        }
      }
      return res.data;
    } catch (err) {
      toast.error('Overlap detection failed');
      return null;
    }
  }, [jobId, sheetId, fetchSheetDetails]);

  return {
    processingDocs, canvasLoading, autoLabeling, labelProgress,
    handleProcessDocuments, handleReview,
    handleCreatePolygon, handleUpdatePolygon, handleDeletePolygon, handleSeedDetect,
    handleUpdateSpace, handleCreateSpace, handleDeleteSpace,
    handlePublish, handleAutoLabel, handleDownload, handleCleanupSpaces, handleClassifySpaceTypes,
    handleDetectOverlaps,
  };
}
